import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { makePost } from "../../utils/fetchUtils";
import { VERIFY_EMAIL_TOKEN, SEND_OTP } from "../../utils/constants";
import nclude from "../../assets/nclude.svg";
import sticker1 from "../../assets/sticker1.svg";
import curve1 from "../../assets/curve.svg";
import greaterThan from "../../assets/greaterthan.svg";
import useWindowWidth from "../../utils/windowWidth";

const VerifyEmailToken = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const mainRef = useRef<HTMLHeadingElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [verifiedEmail, setVerifiedEmail] = useState<string>("");

  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    document.title = "Email Verification - Nclude Portal";
    if (mainRef.current) {
      mainRef.current.focus();
    }
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
      setError("No verification token found in link.");
    }
  }, [token, location]);

  const verifyToken = async (emailToken: string) => {
    setLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      const body = { token: emailToken };
      const resp = await makePost(VERIFY_EMAIL_TOKEN, JSON.stringify(body));
      setLoading(false);

      if (resp && resp.success !== false) {
        setSuccessMsg(resp.message || "Email address verified successfully! Redirecting to home page...");
        if (resp.email) {
          setVerifiedEmail(resp.email);
          setResendEmail(resp.email);
        }
        if (resp.token) {
          localStorage.setItem("token", resp.token);
          if (resp.hasUserInfo) localStorage.setItem("resume", resp.hasUserInfo);
          if (resp.scope) localStorage.setItem("scope", resp.scope);
          if (resp.userId) localStorage.setItem("userId", resp.userId);
        }
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        setError(resp.message || "This verification link has expired.");
      }
    } catch (err: any) {
      setLoading(false);
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError("This verification link has expired.");
      }
    }
  };

  const handleResendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendLoading(true);
    setResendMsg("");
    try {
      const body = { email: resendEmail.trim() };
      const resp = await makePost(SEND_OTP, JSON.stringify(body));
      setResendLoading(false);
      if (resp && resp.success !== false) {
        setResendMsg("A new verification link has been sent to your email.");
      } else {
        setResendMsg(resp.message || "Failed to send new link.");
      }
    } catch (err: any) {
      setResendLoading(false);
      setResendMsg("Failed to send new link. Please try again.");
    }
  };

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  return (
    <div className="auth-page flex items-center login-form" style={{ minHeight: isMobile ? "50vh" : "100vh" }} role="main">
      <div className="logo-container">
        <div className="flex justify-center items-center h-screen w-full">
          <div className="flex flex-col items-start">
            <img src={nclude} alt="Nclude logo" className="logo mb-3" />
            <div className="flex flex-col items-start text-container">
              <img src={sticker1} alt="" aria-hidden="true" className="sticker1 mb-2" />
              <img src={curve1} alt="" aria-hidden="true" className="curve1 mb-4" />
              <h3 className="text-left font-normal">
                Resources <br />
                and opportunities for <span className="font-bold tracking-normal text-white">People with Disabilities</span>
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-form-container flex flex-col items-center">
        <div className="auth-form">
          <h1
            className="mt-12 mb-2 text-[2rem]"
            ref={mainRef}
            tabIndex={-1}
            style={{ fontWeight: 600, lineHeight: "2.375rem" }}
          >
            Email Verification
          </h1>

          {loading ? (
            <div className="py-8 text-center" role="status" aria-live="polite">
              <div className="spinner-border text-primary mb-3" role="status" />
              <p className="text-gray-600 font-medium">Verifying your email address...</p>
            </div>
          ) : successMsg ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                ✓
              </div>
              <h2 className="text-xl font-bold text-green-700 mb-2">
                Email Address Verified!
              </h2>
              <p className="text-gray-600 mb-6" role="status">
                {successMsg} {verifiedEmail && `(${verifiedEmail})`}
              </p>
              <button
                type="button"
                className="bg-[#4f00ec] w-[14rem] h-[3.875rem] rounded submit-button text-white font-semibold mx-auto"
                onClick={() => navigate("/login")}
              >
                Proceed to Login
                <img src={greaterThan} alt="" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="py-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center mb-6">
                <p className="text-[#FF0000] font-semibold text-base mb-1" role="alert">
                  This verification link has expired or is invalid.
                </p>
                <p className="text-gray-600 text-xs">{error}</p>
              </div>

              <div className="mt-6 border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm">
                  Send a New Verification Link
                </h3>
                <form onSubmit={handleResendLink} className="flex flex-col gap-3">
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    className="w-full h-[3.5rem] px-4 border rounded-lg text-sm"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    className="bg-[#5030E5] text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-[#3f20ca] transition-colors"
                    disabled={resendLoading}
                  >
                    {resendLoading ? "Sending New Link..." : "Send a New Link"}
                  </button>
                </form>
                {resendMsg && (
                  <p className="text-center text-xs mt-3 font-semibold text-green-700" role="status">
                    {resendMsg}
                  </p>
                )}
              </div>

              <h2 style={{ fontSize: "1rem", fontWeight: 500 }} className="mt-8 text-right">
                Back to{" "}
                <Link to="/login" className="text-[#5030E5]" style={{ textDecoration: "underline" }}>
                  Login
                </Link>
              </h2>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailToken;
